import { mapState, mapActions, mapGetters, mapMutations } from "vuex";
import AppIcon from "@/components/AppIcon";
import RevenueEvolutionVsPrevPeriod from "@/components/brand_portfolio/RevenueEvolutionVsPrevPeriod";
import SpendEvolutionVsPrevPeriod from "@/components/brand_portfolio/SpendEvolutionVsPrevPeriod";
import Tacos from "@/components/brand_portfolio/Tacos";
import Roas from "@/components/brand_portfolio/Roas";
import AccountsMixin from "@/mixins/AccountsMixin.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import AppPopover from "@/components/AppPopover";
import TrendingPopover from "@/components/brand_portfolio/TrendingPopover.vue";
import TrendingValueWithPopover from "@/components/brand_portfolio/TrendingValueWithPopover.vue";
import LoadingElement from "@/components/shared/LoadingElement.vue";
import CurrencyValue from "@/components/tableElements/CurrencyValue.vue";
import PercentageValue from "@/components/tableElements/PercentageValue.vue";
import { resetLocalStorage } from "@/utils/storage";
import { EVENTS } from "@/utils";
import { APP_PERIOD_DROPDOWN_ELEMENTS } from "@/utils/constants";
import { BRAND_PORTFOLIO } from "@/utils/table_fields";
import AppDropdown from "@/components/AppDropdown.vue";
import { country_codes_with_flags_and_marketplace_ids } from "@/utils/countries";
import SpendBar from "@/components/brand_portfolio/SpendBar.vue";
import BrandPortfolioParentRow from "@/components/brand_portfolio/BrandPortfolioParentRow.vue";
import BrandPortfolioChildrenRow from "@/components/brand_portfolio/BrandPortfolioChildrenRow.vue";
export default {
  name: "BrandPortfolioTable",
  components: {
    AppIcon,
    RevenueEvolutionVsPrevPeriod,
    SpendEvolutionVsPrevPeriod,
    Tacos,
    Roas,
    ChevronDownIcon,
    ChevronRightIcon,
    AppPopover,
    TrendingPopover,
    LoadingElement,
    AppDropdown,
    TrendingValueWithPopover,
    CurrencyValue,
    PercentageValue,
    SpendBar,
    BrandPortfolioParentRow,
    BrandPortfolioChildrenRow
  },
  mixins: [AccountsMixin],
  props: {
    accountType: {
      type: String,
      default: "my_accounts"
    }
  },
  expose: ["refreshPortfolio"],
  emits: ["refresh-sellers-filter"],
  data() {
    return {
      loading: true,
      period: [APP_PERIOD_DROPDOWN_ELEMENTS.DAILY, APP_PERIOD_DROPDOWN_ELEMENTS.WEEKLY, APP_PERIOD_DROPDOWN_ELEMENTS.MONTHLY, APP_PERIOD_DROPDOWN_ELEMENTS.QUARTERLY],
      period_data: {
        daily: "daily_sparkline_data",
        weekly: "weekly_sparkline_data",
        monthly: "monthly_sparkline_data",
        quarterly: "quarterly_sparkline_data"
      },
      period_unit: {
        [APP_PERIOD_DROPDOWN_ELEMENTS.DAILY.value]: APP_PERIOD_DROPDOWN_ELEMENTS.DAILY.unit,
        [APP_PERIOD_DROPDOWN_ELEMENTS.WEEKLY.value]: APP_PERIOD_DROPDOWN_ELEMENTS.WEEKLY.unit,
        [APP_PERIOD_DROPDOWN_ELEMENTS.MONTHLY.value]: APP_PERIOD_DROPDOWN_ELEMENTS.MONTHLY.unit,
        [APP_PERIOD_DROPDOWN_ELEMENTS.QUARTERLY.value]: APP_PERIOD_DROPDOWN_ELEMENTS.QUARTERLY.unit
      },
      selected_period: "weekly",
      hovering_index: -1,
      hovering_workspace_id: "",
      hovering_seller_id: ""
    };
  },
  computed: {
    ...mapState({
      brand_portfolio: state => state.brand_portfolio.list,
      parent_workspace_to_child_workspace_map: state => state.brand_portfolio.parent_workspace_to_child_workspace_map,
      sellers_filter: state => state.brand_portfolio.sellers_filter,
      accounts: state => state.account.list,
      workspaces: state => state.workspace.list,
      isOmnyUser: state => state.auth.isOmnyUser,
      my_workspaces: state => state.workspace.my_workspaces
    }),
    ...mapGetters(["getCountryFlag"]),
    periodLength() {
      return this.selected_period == "14 days" ? 14 : 12;
    },
    columnWidths() {
      return [BRAND_PORTFOLIO.WORKSPACE, BRAND_PORTFOLIO.REVENUE_EVOLUTION_YTD, BRAND_PORTFOLIO.REVENUE_EVOLUTION_MTD, BRAND_PORTFOLIO.REVENUE_EVOLUTION_WTD, BRAND_PORTFOLIO.CURRENT_PERIOD, BRAND_PORTFOLIO.LAST_N_TACOS, BRAND_PORTFOLIO.LAST_N_ROAS, BRAND_PORTFOLIO.PPC_SPEND];
    },
    getWorkspaceList() {
      if (this.accountType === "all_accounts") {
        return this.workspaces.filter(workspace => workspace.workspace_name !== "All Accounts").map(w => w.workspace_id);
      }
      return this.my_workspaces.map(workspace => workspace.workspace_id);
    }
  },
  async mounted() {
    await this.$store.dispatch("workspaceGetMyWorkspaces");
    await this.refreshPortfolio();
  },
  methods: {
    ...mapMutations(["brandPortfolioParentsMapSet"]),
    ...mapActions(["brandPortfolioList", "brandPortfolioGetCurrentWeekBudgetStrategies", "workspaceChange"]),
    workspaceNameForBrandPortfolio(workspace_id) {
      return this.workspaces.find(workspace => workspace.workspace_id == workspace_id)?.workspace_name ?? "-";
    },
    getCountryCode(sales_channel) {
      const countryCode = country_codes_with_flags_and_marketplace_ids.find(marketplace => marketplace?.sales_channel?.toLowerCase() === sales_channel?.toLowerCase())?.country_code;
      return countryCode;
    },
    async refreshPortfolio() {
      try {
        this.loading = true;
        const listResult = await this.brandPortfolioList({
          workspace_list: this.getWorkspaceList,
          is_omny_user: this.isOmnyUser
        });
        const portfolio = listResult?.data ?? [];
        await this.brandPortfolioGetCurrentWeekBudgetStrategies({
          profile_list: portfolio.map(row => row.profile_id).filter(Boolean)
        });
        const workspace_parent = portfolio.filter(portfolio => !portfolio.seller_id);
        const mappedParents = workspace_parent.map(parent => {
          const children = portfolio.filter(portfolio => portfolio.seller_id && portfolio.workspace_id == parent.workspace_id);
          return [{
            ...parent,
            collapsed: true
          }, children];
        });
        this.brandPortfolioParentsMapSet(new Map(mappedParents));
      } catch (error) {
        //console.error("Error refreshing portfolio:", error);
      } finally {
        this.loading = false;
      }
    },
    onWorkspaceRowClickHandler(portfolio) {
      if (portfolio.seller_id) return;
      portfolio.collapsed = !portfolio.collapsed;
    },
    async onWorkspaceNameClickHandler(workspace_id) {
      await this.workspaceChange({
        workspace_id: workspace_id
      });
      resetLocalStorage();
      this.$router.push("/");
      //window.$bus.trigger(EVENTS.REFRESH_APPLICATION);
    },
    async onSellerNameClickHandler(workspace_id, seller_id) {
      localStorage.clear();
      await this.workspaceChange({
        workspace_id: workspace_id
      });
      await this.$emit("refresh-sellers-filter");
      this.sellers_filter.select({
        id: seller_id
      }, "id");
      this.sellers_filter.apply();
      //await this.$nextTick();
      this.$router.push("/");
      // window.$bus.trigger(EVENTS.REFRESH_APPLICATION);
    },
    async onSellerAlertClickHandler(workspace_id, seller_id) {
      await this.workspaceChange({
        workspace_id: workspace_id
      });
      resetLocalStorage();
      this.$router.push("/alerts-page");
      //window.$bus.trigger(EVENTS.REFRESH_APPLICATION);
    },
    async onWorkspaceAlertClickHandler(workspace_id) {
      await this.workspaceChange({
        workspace_id: workspace_id
      });
      resetLocalStorage();
      this.$router.push("/alerts-page");
      //window.$bus.trigger(EVENTS.REFRESH_APPLICATION);
    },
    formatAlertCount(count) {
      return count > 99 ? "+99" : count;
    },
    budgetLeftIndex(portfolio) {
      let borderStyle = "";
      if (portfolio.ppc_forecast_status == "ALERT") {
        borderStyle = "border: 2px dotted var(--color-semantic-blue-800, #1755AB) !important;";
      } else {
        borderStyle = "border: 2px solid var(--color-semantic-blue-800, #1755AB) !important;";
      }
      return `border: ${borderStyle}`;
    }
  }
};